import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { ExchangeFormData, WarehouseInventoryProduct } from '../../../../types/sales.types';
import { salesService } from '../../../../services/sales.service';
import type { SalesCatalogScope } from '../../../sales/SalesStep1/SalesStep1.hooks';

const PRODUCTS_PAGE_SIZE = 50;

export function useExchangeProductSearch(
  sellingLocationId: number | undefined,
  exchangeData: ExchangeFormData,
  catalogScope: SalesCatalogScope,
  canBrowseAllBranches: boolean,
) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<WarehouseInventoryProduct[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [hasMoreProducts, setHasMoreProducts] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const includeWarehouse = catalogScope === 'all_branches' && canBrowseAllBranches;
  const catalogLocationId =
    includeWarehouse || catalogScope === 'all_branches' ? undefined : sellingLocationId;

  const filteredProducts = useMemo(() => {
    const available = availableProducts.filter(
      (product) =>
        !exchangeData.newProducts.some((p) => p.warehouseInventoryProduct.id === product.id),
    );

    if (sellingLocationId == null || catalogScope !== 'all_branches') {
      return available;
    }

    return [...available].sort((a, b) => {
      const aRemote = a.location?.id != null && a.location.id !== sellingLocationId ? 1 : 0;
      const bRemote = b.location?.id != null && b.location.id !== sellingLocationId ? 1 : 0;
      return aRemote - bRemote;
    });
  }, [availableProducts, exchangeData.newProducts, catalogScope, sellingLocationId]);

  const fetchProductsPage = useCallback(
    async (page: number, query: string, append: boolean) => {
      if (sellingLocationId == null && catalogScope === 'selling_branch') {
        setAvailableProducts([]);
        return;
      }

      const response = await salesService.getAvailableProductsPaginated(catalogLocationId, {
        page,
        limit: PRODUCTS_PAGE_SIZE,
        query: query.trim() || undefined,
        includeWarehouse,
      });

      if (append) {
        setAvailableProducts((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const nextItems = response.data.filter((item) => !existingIds.has(item.id));
          return [...prev, ...nextItems];
        });
      } else {
        setAvailableProducts(response.data);
      }

      setProductsPage(page);
      setHasMoreProducts(response.hasMore);
      setShowDropdown(true);
    },
    [catalogLocationId, catalogScope, includeWarehouse, sellingLocationId],
  );

  const loadAvailableProducts = useCallback(async () => {
    if (sellingLocationId == null && catalogScope === 'selling_branch') return;

    setIsLoadingProducts(true);
    try {
      await fetchProductsPage(1, '', false);
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
      setProductsPage(1);
      setHasMoreProducts(false);
    } finally {
      setIsLoadingProducts(false);
    }
  }, [catalogScope, fetchProductsPage, sellingLocationId]);

  const searchProducts = useCallback(
    async (query: string) => {
      if (sellingLocationId == null && catalogScope === 'selling_branch') {
        setAvailableProducts([]);
        return;
      }

      setIsLoadingProducts(true);
      try {
        await fetchProductsPage(1, query, false);
      } catch (error) {
        console.error('Error searching products:', error);
        setAvailableProducts([]);
        setProductsPage(1);
        setHasMoreProducts(false);
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [catalogScope, fetchProductsPage, sellingLocationId],
  );

  const loadNextProductsPage = useCallback(async () => {
    if (
      (sellingLocationId == null && catalogScope === 'selling_branch') ||
      isLoadingProducts ||
      isLoadingMoreProducts ||
      !hasMoreProducts
    ) {
      return;
    }

    setIsLoadingMoreProducts(true);
    try {
      await fetchProductsPage(productsPage + 1, searchQuery, true);
    } catch (error) {
      console.error('Error loading more products:', error);
    } finally {
      setIsLoadingMoreProducts(false);
    }
  }, [
    catalogScope,
    fetchProductsPage,
    hasMoreProducts,
    isLoadingMoreProducts,
    isLoadingProducts,
    productsPage,
    searchQuery,
    sellingLocationId,
  ]);

  const handleDropdownScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const threshold = 24;
      if (target.scrollHeight - target.scrollTop - target.clientHeight <= threshold) {
        loadNextProductsPage();
      }
    },
    [loadNextProductsPage],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Free-text search must keep spaces; barcode decode is only for scan-dedicated fields.
      const value = e.target.value;
      setSearchQuery(value);
      searchProducts(value);
    },
    [searchProducts],
  );

  const handleSearchFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setAnchorEl(e.currentTarget);
      if (availableProducts.length === 0) {
        loadAvailableProducts();
      } else {
        setShowDropdown(true);
      }
    },
    [availableProducts.length, loadAvailableProducts],
  );

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowDropdown(false), 200);
  }, []);

  const resetSearchAfterSelect = useCallback(() => {
    setSearchQuery('');
    setAvailableProducts([]);
    setShowDropdown(false);
  }, []);

  const resetCatalogResults = useCallback(() => {
    setSearchQuery('');
    setAvailableProducts([]);
    setShowDropdown(false);
    setProductsPage(1);
    setHasMoreProducts(false);
  }, []);

  useEffect(() => {
    resetCatalogResults();
  }, [catalogScope, resetCatalogResults]);

  return {
    searchQuery,
    showDropdown,
    filteredProducts,
    isLoadingProducts,
    isLoadingMoreProducts,
    anchorEl,
    searchInputRef,
    handleSearchChange,
    handleSearchFocus,
    handleSearchBlur,
    handleDropdownScroll,
    resetSearchAfterSelect,
  };
}
